import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import type { Recording } from '@jabberr/core';
import { EmptyState } from '../components/EmptyState.js';
import { Badge, ErrorBox, Spinner } from '../components/ui.js';
import { api, errorMessage } from '../lib/api.js';
import { formatBytes, mmss, relativeTime } from '../lib/format.js';
import { ChunkUploader, sliceFile, type UploaderStatus } from '../lib/recorder.js';
import { useDrawer } from '../state/drawer.js';

import { useMeetingRecorder } from '../state/meeting-recorder.js';

function tone(status: Recording['status']): 'neutral' | 'ok' | 'warn' | 'bad' {
  switch (status) {
    case 'ready':
      return 'ok';
    case 'failed':
      return 'bad';
    case 'queued':
    case 'transcribing':
      return 'warn';
    default:
      return 'neutral';
  }
}

export function RecordingsPage() {
  const { projectId = '' } = useParams();
  const drawer = useDrawer();
  const meeting = useMeetingRecorder();
  const [recordings, setRecordings] = useState<Recording[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { recordings: list } = await api.recordings.list(projectId);
      setRecordings(list);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [projectId]);

  useEffect(() => {
    setRecordings(null);
    void load();
  }, [load]);

  useEffect(() => {
    drawer.setInfo({ projectId, title: 'Recordings' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Poll while work is pending (fast), slower once everything is settled.
  const pending = (recordings ?? []).some((r) => r.status === 'queued' || r.status === 'transcribing');
  useEffect(() => {
    const t = setInterval(() => void load(), pending ? 3000 : 20000);
    return () => clearInterval(t);
  }, [load, pending]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Recordings</h1>
          <p className="page-subtitle muted small">
            Record here, upload a file, or paste a Teams/Zoom .vtt. Audio is deleted 30 days after upload; transcripts stay. Never
            record without telling the room.
          </p>
        </div>
      </header>
      {error ? <ErrorBox>{error}</ErrorBox> : null}

      <div className="rec-grid">
        <section className="card"><h2>Record a meeting</h2><p className="muted small">Capture your microphone or shared meeting audio. The recorder stays open while you use Jabberr.</p><button type="button" className="btn btn-primary" onClick={() => meeting.open(projectId)} aria-label="Open meeting recorder">Open meeting recorder</button></section>
        <FileUpload projectId={projectId} onDone={() => void load()} onError={setError} />
        <VttImport projectId={projectId} onDone={() => void load()} onError={setError} />
      </div>

      <section>
        <div className="section-head">
          <h2>All recordings</h2>
        </div>
        {recordings === null ? (
          <Spinner label="Loading" />
        ) : recordings.length === 0 ? (
          <EmptyState title="Nothing recorded yet." />
        ) : (
          <ul className="list">
            {recordings.map((r) => (
              <li key={r.id} className="list-item">
                <Link to={`/r/${r.id}`} className="list-main">
                  <span className="list-title">{r.title}</span>
                  <span className="muted small">
                    {r.source} · {r.durationS != null ? `${mmss(r.durationS)} · ` : ''}
                    {r.sizeBytes != null ? `${formatBytes(r.sizeBytes)} · ` : ''}
                    {r.chunkCount ? `${r.chunkCount} chunks · ` : ''}
                    {relativeTime(r.createdAt)}
                    {r.status === 'queued' ? ' · waiting for a worker' : ''}
                    {r.error ? ` · ${r.error}` : ''}
                  </span>
                </Link>
                <Badge tone={tone(r.status)}>{r.status}</Badge>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

interface SubProps {
  projectId: string;
  onDone: () => void;
  onError: (m: string) => void;
}

function FileUpload({ projectId, onDone, onError }: SubProps) {
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<UploaderStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const mime = file.type || 'application/octet-stream';
      const { recordingId } = await api.recordings.create(projectId, { title: title.trim() || file.name, source: 'upload', mime });
      const slices = sliceFile(file);
      const up = new ChunkUploader({
        upload: (s, blob, signal) => api.recordings.putChunk(recordingId, s, blob, signal).then(() => undefined),
        onStatus: setProgress,
        maxAttempts: 8,
      });
      slices.forEach((b, i) => up.enqueue(i, b));
      await up.drain();
      await api.recordings.complete(recordingId, { chunkCount: slices.length });
      setFile(null);
      setTitle('');
      setProgress(null);
      onDone();
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>Upload a file</h2>
      <form className="stack" onSubmit={(e) => void submit(e)}>
        <input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Upload title" />
        <input type="file" accept="audio/*,video/*,.webm,.m4a,.mp3,.wav,.mp4" onChange={(e) => setFile(e.target.files?.[0] ?? null)} aria-label="Audio file" />
        <div className="row">
          <button type="submit" className="btn btn-primary" disabled={!file || busy}>
            Upload
          </button>
          {progress ? (
            <span className="muted small">
              {progress.uploaded} / {progress.uploaded + progress.pending} parts{progress.failing ? ' · retrying' : ''}
            </span>
          ) : file ? (
            <span className="muted small">{formatBytes(file.size)}</span>
          ) : null}
        </div>
      </form>
    </section>
  );
}

function VttImport({ projectId, onDone, onError }: SubProps) {
  const [title, setTitle] = useState('');
  const [vtt, setVtt] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.recordings.importVtt(projectId, { title: title.trim() || 'Imported transcript', vtt });
      setVtt('');
      setTitle('');
      onDone();
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card">
      <h2>Import a Teams or Zoom .vtt</h2>
      <form className="stack" onSubmit={(e) => void submit(e)}>
        <input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="VTT title" />
        <textarea
          rows={6}
          value={vtt}
          onChange={(e) => setVtt(e.target.value)}
          placeholder={'WEBVTT\n\n00:00:01.000 --> 00:00:04.000\n<v John Sarver>Let us start with the decision.'}
          aria-label="WebVTT text"
          className="mono"
        />
        <div className="row">
          <button type="submit" className="btn btn-primary" disabled={!vtt.trim() || busy}>
            Import
          </button>
          <span className="muted small">Voice tags become speaker names; no transcription job is needed.</span>
        </div>
      </form>
    </section>
  );
}
